/**
 * Cart Drawer Discount Functionality
 */
class CartDrawerDiscount {
    constructor() {
      this.init();
    }
    
    init() {
      // Query all elements fresh
      this.queryElements();
      
      if (this.discountInput && this.discountButton) {
        this.attachEventListeners();
      }
      
      if (this.discountCancelButton) {
        this.attachCancelListener();
      }

      if (this.discountToggle && this.discountBox) {
        this.attachToggleListener();
      }
      
      // Attach listeners for individual discount removal
      this.attachSingleDiscountListeners();
      
      // Sync sessionStorage with current cart discount codes
      this.syncDiscountCodes();
      
      // Initialize gift drawer observer
      this.initializeGiftDrawerObserver();
    }
    
    queryElements() {
      // Query all DOM elements fresh each time
      this.discountInput = document.querySelector('.cart-drawer__discount-input');
      this.discountButton = document.querySelector('.cart-drawer__discount-button');
      this.discountError = document.querySelector('.cart-drawer__discount-error');
      this.discountCancelButton = document.querySelector('[data-action="remove-discount"]');
      this.discountToggle = document.querySelector('.cart-drawer__discount-toggle');
      this.discountBox = document.querySelector('.cart-drawer__discount-box');
    }

    attachToggleListener() {
      // Remove any existing listener first
      if (this.boundToggleListener) {
        this.discountToggle.removeEventListener('click', this.boundToggleListener);
      }
      
      // Ensure discount box starts closed
      this.discountBox.classList.remove('is-active');
      this.discountToggle.classList.remove('is-active');
      this.discountBox.style.height = '0';
      
      // Get the inner content height for smooth animation
      const inner = this.discountBox.querySelector('.cart-drawer__discount-inner');
      if (inner) {
        const height = inner.offsetHeight;
        this.discountBox.style.setProperty('--discount-height', height + 'px');
      }
      
      this.boundToggleListener = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const isActive = this.discountBox.classList.contains('is-active');
        
        if (!isActive) {
          // Opening - set exact height for smooth animation
          const inner = this.discountBox.querySelector('.cart-drawer__discount-inner');
          if (inner) {
            const height = inner.offsetHeight;
            this.discountBox.style.height = height + 'px';
          }
          this.discountBox.classList.add('is-active');
          this.discountToggle.classList.add('is-active');
          
          // Focus after animation
          setTimeout(() => {
            if (this.discountInput) {
              this.discountInput.focus();
            }
          }, 350);
        } else {
          // Closing - animate to 0
          this.discountBox.style.height = '0';
          this.discountBox.classList.remove('is-active');
          this.discountToggle.classList.remove('is-active');
        }
      };
      this.discountToggle.addEventListener('click', this.boundToggleListener);
      
      // Mark that listener is attached
      this.discountToggle.setAttribute('data-listener-attached', 'true');
    
    }
    
    async syncDiscountCodes() {
      try {
        // Get current cart state
        const response = await fetch('/cart.js', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        if (response.ok) {
          const cartData = await response.json();
          
          // If cart has discount codes, sync with sessionStorage
          if (cartData.discount_codes && Array.isArray(cartData.discount_codes)) {
            const appliedCodes = cartData.discount_codes
              .filter(discount => discount.applicable === true)
              .map(discount => discount.code)
              .join(',');
            
            if (appliedCodes) {
              sessionStorage.setItem('appliedDiscountCodes', appliedCodes);
            } else {
              sessionStorage.removeItem('appliedDiscountCodes');
            }
          } else {
            // No discount codes in cart, clear sessionStorage
            sessionStorage.removeItem('appliedDiscountCodes');
          }
        }
      } catch (error) {
        console.error('Error syncing discount codes:', error);
      }
    }
    
    attachEventListeners() {
      // Remove any existing listeners first
      this.removeEventListeners();
      
      // Store bound functions for removal later
      this.boundHandleKeypress = this.handleKeypress.bind(this);
      this.boundHandlePaste = this.handlePaste.bind(this);
      this.boundHandleKeydown = this.handleKeydown.bind(this);
      this.boundApplyDiscount = this.applyDiscount.bind(this);
      
      // Prevent comma input
      this.discountInput.addEventListener('keypress', this.boundHandleKeypress);
      
      // Handle paste to remove commas
      this.discountInput.addEventListener('paste', this.boundHandlePaste);
      
      // Handle Enter key
      this.discountInput.addEventListener('keydown', this.boundHandleKeydown);
      
      // Handle button click
      this.discountButton.addEventListener('click', this.boundApplyDiscount);
    }
    
    attachCancelListener() {
      if (this.boundRemoveDiscount) {
        this.discountCancelButton.removeEventListener('click', this.boundRemoveDiscount);
      }
      
      this.boundRemoveDiscount = this.removeDiscount.bind(this);
      this.discountCancelButton.addEventListener('click', this.boundRemoveDiscount);
    }
    
    removeEventListeners() {
      if (this.discountInput) {
        if (this.boundHandleKeypress) {
          this.discountInput.removeEventListener('keypress', this.boundHandleKeypress);
        }
        if (this.boundHandlePaste) {
          this.discountInput.removeEventListener('paste', this.boundHandlePaste);
        }
        if (this.boundHandleKeydown) {
          this.discountInput.removeEventListener('keydown', this.boundHandleKeydown);
        }
      }
      
      if (this.discountButton && this.boundApplyDiscount) {
        this.discountButton.removeEventListener('click', this.boundApplyDiscount);
      }
      
      if (this.discountCancelButton && this.boundRemoveDiscount) {
        this.discountCancelButton.removeEventListener('click', this.boundRemoveDiscount);
      }
      
      if (this.discountToggle && this.boundToggleListener) {
        this.discountToggle.removeEventListener('click', this.boundToggleListener);
        this.discountToggle.removeAttribute('data-listener-attached');
      }
      
      // Remove single discount listeners
      if (this.boundRemoveSingleDiscount) {
        const singleDiscountButtons = document.querySelectorAll('[data-action="remove-single-discount"]');
        singleDiscountButtons.forEach(button => {
          button.removeEventListener('click', this.boundRemoveSingleDiscount);
        });
      }
      
      // Disconnect gift drawer observer
      if (this.giftDrawerObserver) {
        this.giftDrawerObserver.disconnect();
      }
    }
    
    attachSingleDiscountListeners() {
      const singleDiscountButtons = document.querySelectorAll('[data-action="remove-single-discount"]');
      
      if (singleDiscountButtons.length > 0) {
        this.boundRemoveSingleDiscount = this.removeSingleDiscount.bind(this);
        
        singleDiscountButtons.forEach(button => {
          button.addEventListener('click', this.boundRemoveSingleDiscount);
        });
      }
    }
    
    async removeSingleDiscount(e) {
      const discountToRemove = e.currentTarget.dataset.discountCode;
      
      if (!discountToRemove) {
        return;
      }
      
      try {
        // Get current discount codes from sessionStorage
        const existingDiscounts = sessionStorage.getItem('appliedDiscountCodes');
        
        if (!existingDiscounts) {
          return;
        }
        
        // Split existing discounts and filter out the one to remove
        const discountArray = existingDiscounts.split(',');
        const remainingDiscounts = discountArray.filter(code => 
          code.toLowerCase() !== discountToRemove.toLowerCase()
        );
        
        // If no discounts remain, remove all
        if (remainingDiscounts.length === 0) {
          await this.removeDiscount();
          return;
        }
        
        // Join remaining discounts
        const discountsToApply = remainingDiscounts.join(',');
        
        // Update cart with remaining discounts
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            discount: discountsToApply
          })
        });
        
        if (response.ok) {
          // Update sessionStorage
          sessionStorage.setItem('appliedDiscountCodes', discountsToApply);
          
          // Refresh cart
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        }
      } catch (error) {
        console.error('Single discount removal error:', error);
      }
    }
    
    handleKeypress(e) {
      if (e.key === ',') {
        e.preventDefault();
      }
    }
    
    handlePaste(e) {
      e.preventDefault();
      const pastedText = (e.clipboardData || window.clipboardData).getData('text');
      const cleanedText = pastedText.replace(/,/g, '');
      this.discountInput.value = cleanedText;
    }
    
    handleKeydown(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.applyDiscount();
      }
    }
    
    async applyDiscount() {
      const discountCode = this.discountInput.value.trim();
      
      if (!discountCode) {
        return;
      }
      
      // Get existing discount codes from sessionStorage
      const existingDiscounts = sessionStorage.getItem('appliedDiscountCodes');
      let discountsToApply = discountCode;
      
      // Combine with existing discounts if any
      if (existingDiscounts) {
        // Check if this discount code is already applied
        const existingArray = existingDiscounts.split(',');
        if (!existingArray.some(code => code.toLowerCase() === discountCode.toLowerCase())) {
          discountsToApply = existingDiscounts + ',' + discountCode;
        } else {
          // Discount already applied, just return
          this.discountInput.value = '';
          return;
        }
      }
      
      // Disable button and input during request
      this.discountButton.disabled = true;
      this.discountInput.disabled = true;
      
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            discount: discountsToApply
          })
        });
        
        if (response.ok) {
          const cartData = await response.json();
          
          // Check if discount code was applied successfully
          let discountApplied = false;
          
          // Check the discount_codes array for our code
          if (cartData.discount_codes && Array.isArray(cartData.discount_codes)) {
            const appliedDiscount = cartData.discount_codes.find(discount => 
              discount.code && discount.code.toLowerCase() === discountCode.toLowerCase()
            );
            
            // Check if the discount is applicable
            discountApplied = appliedDiscount && appliedDiscount.applicable === true;
          }
          
          if (discountApplied) {
            // Store all applied discount codes in sessionStorage
            const appliedCodes = cartData.discount_codes
              .filter(discount => discount.applicable === true)
              .map(discount => discount.code)
              .join(',');
            
            sessionStorage.setItem('appliedDiscountCodes', appliedCodes);
            
            // Success - refresh cart
            document.dispatchEvent(new CustomEvent("cart:refresh"));
            
            // Clear input
            this.discountInput.value = '';
          } else {
            // Discount code not applicable
            this.showError();
          }
        } else {
          // Show error
          this.showError();
        }
      } catch (error) {
        console.error('Discount application error:', error);
        // Show error
        this.showError();
      } finally {
        // Re-enable button and input
        this.discountButton.disabled = false;
        this.discountInput.disabled = false;
        this.discountInput.focus();
      }
    }
    
    async removeDiscount() {
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            discount: ''
          })
        });
        
        if (response.ok) {
          // Clear discount codes from sessionStorage
          sessionStorage.removeItem('appliedDiscountCodes');
          
          // Success - refresh cart
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        }
      } catch (error) {
        console.error('Discount removal error:', error);
      }
    }
    
    showError() {
      // Show error message
      this.discountError.hidden = false;
      
      // Adjust height to accommodate error message
      if (this.discountBox.classList.contains('is-active')) {
        const inner = this.discountBox.querySelector('.cart-drawer__discount-inner');
        if (inner) {
          const height = inner.offsetHeight;
          this.discountBox.style.height = height + 'px';
        }
      }

      // Hide error after 5 seconds
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
      }
      
      this.errorTimeout = setTimeout(() => {
        this.discountError.hidden = true;
        
        // Readjust height after error is hidden
        if (this.discountBox.classList.contains('is-active')) {
          const inner = this.discountBox.querySelector('.cart-drawer__discount-inner');
          if (inner) {
            const height = inner.offsetHeight;
            this.discountBox.style.height = height + 'px';
          }
        }
      }, 5000);
    }
    
    initializeGiftDrawerObserver() {
      const giftDrawer = document.querySelector('#gift-bar-drawer');
      const footerWrapper = document.querySelector('.hv-cart-drawer__footer-wrapper');
      const headerWrapper = document.querySelector('.hv-cart-drawer .header-title');
      const cartDrawer = document.querySelector('cart-drawer');
      
      if (!giftDrawer || !footerWrapper) {
        return;
      }
      
      // Function to toggle elements visibility
      const toggleElements = (isActive) => {
        footerWrapper.style.display = isActive ? 'none' : '';
        if (headerWrapper) {
          headerWrapper.style.display = isActive ? 'none' : '';
        }
        
        // Access shadow DOM to hide close button
        if (cartDrawer && cartDrawer.shadowRoot) {
          const closeButton = cartDrawer.shadowRoot.querySelector('[part~="close-button"]');
          if (closeButton) {
            closeButton.style.display = isActive ? 'none' : '';
          }
        }
      };
      
      // Initial check
      if (giftDrawer.classList.contains('active')) {
        toggleElements(true);
      }
      
      // Create observer to watch for class changes
      this.giftDrawerObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const isActive = giftDrawer.classList.contains('active');
            toggleElements(isActive);
          }
        });
      });
      
      // Start observing
      this.giftDrawerObserver.observe(giftDrawer, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
    
    destroy() {
      this.removeEventListeners();
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
      }
    }
  }
  
  // Global instance
  let cartDrawerDiscountInstance = null;
  
  // Function to initialize or reinitialize
  function initializeCartDrawerDiscount() {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Destroy existing instance and create new one
      if (cartDrawerDiscountInstance) {
        cartDrawerDiscountInstance.destroy();
        cartDrawerDiscountInstance = null;
      }
      
      // Check if discount box elements exist before creating instance
      const discountBox = document.querySelector('.cart-drawer__discount-box');
      const discountToggle = document.querySelector('.cart-drawer__discount-toggle');
      
      if (discountBox || discountToggle) {
        cartDrawerDiscountInstance = new CartDrawerDiscount();
      }
    });
  }
  
  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', initializeCartDrawerDiscount);
  
  // Reinitialize when cart is refreshed
  document.addEventListener('cart:refresh', () => {
    // Use a longer delay to ensure cart drawer is fully rendered
    setTimeout(() => {
      initializeCartDrawerDiscount();
    }, 800);
  });
  
  document.addEventListener('cart:change', () => {
    // Use a longer delay to ensure cart drawer is fully rendered
    setTimeout(() => {
      initializeCartDrawerDiscount();
    }, 800);
  });
  
  
  
  // ADDITIONAL EVENTS FOR THEME CUSTOMIZER
  // Handle Shopify section events for theme editor
  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail.sectionId.includes('cart-drawer')) {
      setTimeout(initializeCartDrawerDiscount, 500);
    }
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.detail.sectionId.includes('cart-drawer') && cartDrawerDiscountInstance) {
      cartDrawerDiscountInstance.destroy();
    }
  });
  
  // Add a mutation observer to handle dynamic content updates
  let cartObserver = null;
  
  function observeCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    
    if (!cartDrawer) {
      return;
    }
    
    // Disconnect existing observer if any
    if (cartObserver) {
      cartObserver.disconnect();
    }
    
    cartObserver = new MutationObserver((mutations) => {
      // Check if discount box was added/removed
      const hasDiscountBox = document.querySelector('.cart-drawer__discount-box');
      const hasInstance = cartDrawerDiscountInstance !== null;
      
      if (hasDiscountBox && !hasInstance) {
        // Discount box was added but no instance exists
        initializeCartDrawerDiscount();
      } else if (hasDiscountBox && hasInstance) {
        // Check if toggle is still working
        const toggle = document.querySelector('.cart-drawer__discount-toggle');
        if (toggle && !toggle.hasAttribute('data-listener-attached')) {
          // Re-initialize if toggle lost its listener
          initializeCartDrawerDiscount();
        }
      }
    });
    
    cartObserver.observe(cartDrawer, {
      childList: true,
      subtree: true
    });
  }
  
  // Start observing when DOM is ready
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(observeCartDrawer, 1000);
  }); 